import { useEffect, useRef, useState } from "react";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasApi } from "../../lib/canvas-api";
import { saveCanvasThumbnail } from "../../lib/canvas-thumbnail-cache";
import { dataUrlToPngBlob, renderCanvasThumbnailDataUrl } from "../../lib/canvas-thumbnail";
import { getWebConfig } from "../../env";

const UPLOAD_DEBOUNCE_MS = 2_500;

export function useCanvasThumbnail(canvasId: string): string | undefined {
  const activeCanvasId = useCanvasDocStore((s) => s.canvasId);
  const objects = useCanvasDocStore((s) => s.objects);
  const updateLocalMeta = useCanvasDocStore((s) => s.updateLocalMeta);
  const canvasApi = useCanvasApi();
  const [thumbnail, setThumbnail] = useState<string | undefined>();
  const latestDataUrlRef = useRef<string | undefined>(undefined);
  const uploadTimerRef = useRef<number | undefined>(undefined);
  const uploadGenerationRef = useRef(0);

  useEffect(() => {
    if (activeCanvasId !== canvasId) return;
    const dataUrl = renderCanvasThumbnailDataUrl(Object.values(objects));
    if (!dataUrl) return;
    latestDataUrlRef.current = dataUrl;
    setThumbnail(dataUrl);
    saveCanvasThumbnail(canvasId, dataUrl);

    if (uploadTimerRef.current !== undefined) {
      window.clearTimeout(uploadTimerRef.current);
    }
    // 並行アップロードの古い完了通知が最新サムネイルのメタ情報を戻さないよう、
    // タイマーを予約した時点の世代だけがメタ更新できるようにする。
    const uploadGeneration = (uploadGenerationRef.current += 1);
    uploadTimerRef.current = window.setTimeout(() => {
      uploadTimerRef.current = undefined;
      void uploadThumbnail(
        canvasApi,
        canvasId,
        latestDataUrlRef.current,
        updateLocalMeta,
        () => uploadGeneration === uploadGenerationRef.current
      );
    }, UPLOAD_DEBOUNCE_MS);

    return () => {
      uploadGenerationRef.current += 1;
      if (uploadTimerRef.current !== undefined) {
        window.clearTimeout(uploadTimerRef.current);
        uploadTimerRef.current = undefined;
      }
    };
  }, [activeCanvasId, canvasApi, canvasId, objects, updateLocalMeta]);

  return thumbnail;
}

async function uploadThumbnail(
  canvasApi: ReturnType<typeof useCanvasApi>,
  canvasId: string,
  dataUrl: string | undefined,
  updateLocalMeta: (meta: { thumbnailKey?: string | undefined; thumbnailUrl?: string | undefined }) => void,
  isLatestUpload: () => boolean
): Promise<void> {
  if (!dataUrl) return;
  // mock ローカルでは S3 が無いことがあるため、TOP 表示用の localStorage キャッシュだけ更新する。
  if (getWebConfig().authMode === "mock") return;
  const blob = dataUrlToPngBlob(dataUrl);
  if (!blob) return;
  try {
    const presigned = await canvasApi.presignThumbnailUpload({
      canvasId,
      contentLength: blob.size,
    });
    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: blob,
      headers: { "content-type": "image/png" },
    });
    if (!uploadResponse.ok) {
      throw new Error(`サムネイルアップロードに失敗しました: HTTP ${uploadResponse.status}`);
    }
    if (!isLatestUpload()) return;
    const result = await canvasApi.updateCanvasMeta(canvasId, { thumbnailKey: presigned.key });
    if (!isLatestUpload()) return;
    updateLocalMeta({
      thumbnailKey: result.meta.thumbnailKey,
      thumbnailUrl: result.meta.thumbnailUrl,
    });
  } catch (error) {
    console.warn("[thumbnail] サムネイルのアップロードに失敗しました", error);
  }
}
